import {
  DEFAULT_DATE_RANGE_PRESET,
  getDateRangeForPreset,
  type DateRangePreset,
} from '../../utils/dateRangePresets';
import type { CustomerPaymentMethod } from '../../types/customerPayment';

export type StatusFilter =
  | 'all'
  | 'open'
  | 'overdue'
  | 'draft'
  | 'partial'
  | 'paid'
  | 'voided'
  | 'sent'
  | 'accepted'
  | 'rejected'
  | 'expired';
export type TypeFilter = 'all' | 'invoice' | 'payment' | 'quotation';
export type PaymentMethodFilter = '' | CustomerPaymentMethod;
export type SortKey =
  | 'transaction_id'
  | 'transaction_type'
  | 'number'
  | 'txn_date'
  | 'customer_name'
  | 'total_amount'
  | 'balance'
  | 'status'
  | 'payment_method';
export type SortDir = 'asc' | 'desc';

export type SalesTransactionFilters = {
  statusFilter: StatusFilter;
  typeFilter: TypeFilter;
  paymentMethodFilter: PaymentMethodFilter;
  customerFilter: string;
  page: number;
  sortKey: SortKey;
  sortDir: SortDir;
  datePreset: DateRangePreset;
  startDate: string;
  endDate: string;
  searchQuery: string;
};

const defaultDateRange = getDateRangeForPreset(DEFAULT_DATE_RANGE_PRESET);

export const DEFAULT_SALES_TRANSACTION_FILTERS: SalesTransactionFilters = {
  statusFilter: 'all',
  typeFilter: 'all',
  paymentMethodFilter: '',
  customerFilter: '',
  page: 1,
  sortKey: 'txn_date',
  sortDir: 'desc',
  datePreset: DEFAULT_DATE_RANGE_PRESET,
  startDate: defaultDateRange.startDate,
  endDate: defaultDateRange.endDate,
  searchQuery: '',
};

const STATUS_VALUES: StatusFilter[] = [
  'all',
  'open',
  'overdue',
  'draft',
  'partial',
  'paid',
  'voided',
  'sent',
  'accepted',
  'rejected',
  'expired',
];

const TYPE_VALUES: TypeFilter[] = ['all', 'invoice', 'payment', 'quotation'];

const INVOICE_PAYMENT_STATUS_FILTERS: StatusFilter[] = [
  'all',
  'open',
  'overdue',
  'draft',
  'partial',
  'paid',
  'voided',
];

const QUOTATION_STATUS_FILTERS: StatusFilter[] = [
  'all',
  'draft',
  'sent',
  'accepted',
  'rejected',
  'expired',
];

export const isQuotationOnlyStatus = (status: StatusFilter): boolean =>
  status === 'sent' || status === 'accepted' || status === 'rejected' || status === 'expired';

export const isInvoicePaymentOnlyStatus = (status: StatusFilter): boolean =>
  status === 'open' ||
  status === 'overdue' ||
  status === 'partial' ||
  status === 'paid' ||
  status === 'voided';

export const isStatusCompatibleWithType = (
  status: StatusFilter,
  typeFilter: TypeFilter
): boolean => {
  if (status === 'all') return true;
  if (typeFilter === 'quotation') {
    return !isInvoicePaymentOnlyStatus(status) || status === 'draft';
  }
  if (typeFilter === 'invoice' || typeFilter === 'payment') {
    return !isQuotationOnlyStatus(status);
  }
  return true;
};

export const getStatusTabsForType = (typeFilter: TypeFilter): StatusFilter[] => {
  if (typeFilter === 'quotation') return QUOTATION_STATUS_FILTERS;
  if (typeFilter === 'invoice' || typeFilter === 'payment') {
    return INVOICE_PAYMENT_STATUS_FILTERS;
  }
  return [
    'all',
    'open',
    'overdue',
    'draft',
    'partial',
    'paid',
    'voided',
    'sent',
    'accepted',
    'rejected',
    'expired',
  ];
};

const SORT_KEYS: SortKey[] = [
  'transaction_id',
  'transaction_type',
  'number',
  'txn_date',
  'customer_name',
  'total_amount',
  'balance',
  'status',
  'payment_method',
];

const PAYMENT_METHODS: CustomerPaymentMethod[] = [
  'CASH',
  'CARD',
  'BANK_TRANSFER',
  'CHEQUE',
  'ONLINE',
];

const DATE_PRESETS: DateRangePreset[] = [
  'today',
  'yesterday',
  'this_week',
  'last_week',
  'this_month',
  'last_month',
  'last_30_days',
  'this_quarter',
  'last_quarter',
  'last_3_months',
  'last_6_months',
  'last_12_months',
  'this_year',
  'last_year',
  'all',
  'custom',
];

const parseEnum = <T extends string>(value: string | null, allowed: readonly T[], fallback: T): T =>
  allowed.includes(value as T) ? (value as T) : fallback;

const parsePositiveInt = (value: string | null, fallback: number) => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
};

export const parseSalesTransactionFilters = (
  params: URLSearchParams
): SalesTransactionFilters => {
  const datePreset = parseEnum(params.get('preset'), DATE_PRESETS, DEFAULT_DATE_RANGE_PRESET);

  let startDate = defaultDateRange.startDate;
  let endDate = defaultDateRange.endDate;

  if (datePreset === 'custom') {
    startDate = params.get('from') ?? '';
    endDate = params.get('to') ?? '';
  } else {
    const range = getDateRangeForPreset(datePreset);
    startDate = range.startDate;
    endDate = range.endDate;
  }

  return {
    statusFilter: parseEnum(params.get('status'), STATUS_VALUES, 'all'),
    typeFilter: parseEnum(params.get('type'), TYPE_VALUES, 'all'),
    paymentMethodFilter: (() => {
      const method = params.get('method');
      if (!method) return '';
      return PAYMENT_METHODS.includes(method as CustomerPaymentMethod)
        ? (method as CustomerPaymentMethod)
        : '';
    })(),
    customerFilter: params.get('customer')?.trim() ?? '',
    page: parsePositiveInt(params.get('page'), 1),
    sortKey: parseEnum(params.get('sort'), SORT_KEYS, 'txn_date'),
    sortDir: params.get('dir') === 'asc' ? 'asc' : 'desc',
    datePreset,
    startDate,
    endDate,
    searchQuery: params.get('q')?.trim() ?? '',
  };
};

const isDefault = <K extends keyof SalesTransactionFilters>(
  key: K,
  value: SalesTransactionFilters[K]
) => value === DEFAULT_SALES_TRANSACTION_FILTERS[key];

export const buildSalesTransactionSearchParams = (
  filters: SalesTransactionFilters
): URLSearchParams => {
  const params = new URLSearchParams();

  if (!isDefault('statusFilter', filters.statusFilter)) {
    params.set('status', filters.statusFilter);
  }
  if (!isDefault('typeFilter', filters.typeFilter)) {
    params.set('type', filters.typeFilter);
  }
  if (filters.paymentMethodFilter) {
    params.set('method', filters.paymentMethodFilter);
  }
  if (filters.customerFilter) {
    params.set('customer', filters.customerFilter);
  }
  if (!isDefault('page', filters.page)) {
    params.set('page', String(filters.page));
  }
  if (!isDefault('sortKey', filters.sortKey)) {
    params.set('sort', filters.sortKey);
  }
  if (!isDefault('sortDir', filters.sortDir)) {
    params.set('dir', filters.sortDir);
  }
  if (!isDefault('datePreset', filters.datePreset)) {
    params.set('preset', filters.datePreset);
  }
  if (filters.datePreset === 'custom') {
    if (filters.startDate) params.set('from', filters.startDate);
    if (filters.endDate) params.set('to', filters.endDate);
  }
  if (filters.searchQuery) {
    params.set('q', filters.searchQuery);
  }

  return params;
};

export const mergeSalesTransactionFilters = (
  current: URLSearchParams,
  patch: Partial<SalesTransactionFilters>
): URLSearchParams => {
  const merged: SalesTransactionFilters = {
    ...parseSalesTransactionFilters(current),
    ...patch,
  };

  if (patch.datePreset != null && patch.datePreset !== 'custom' && patch.startDate == null) {
    const range = getDateRangeForPreset(patch.datePreset);
    merged.startDate = range.startDate;
    merged.endDate = range.endDate;
  }

  return buildSalesTransactionSearchParams(merged);
};
